// src/pages/outsourcing_management/InquiryOrderPicker.tsx
//
// Attaching a new inquiry to an existing order. Create-time only: the update
// payload carries no orderId/quoteId, and moving the link would strand the
// item lines already on the inquiry, so InquiryDialog renders this only while
// raising one.
//
// Lifted out of InquiryComponent unchanged in behaviour, including the two
// workarounds it carried: the page size is deliberately absurd because the API
// reports neither a total nor a hasMore flag, and the returned rows are
// filtered again here because the server-side search matches more than the
// term.
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  Input,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { OrderType } from "types/OrderTypes";
import { apiGetOrder, apiGetOrders } from "util/network/Orders";

type OrderHit = {
  id: number;
  orderNo: string;
  firstName: string;
  lastName: string;
};

type PropsType = {
  /** The order this inquiry is already attached to, if it opened from one. */
  order: OrderType | undefined;
  /** Called with the chosen order, or undefined while the field is being typed in. */
  onPick: (order: OrderType | undefined) => void;
};

const InquiryOrderPicker: FunctionComponent<PropsType> = ({
  order,
  onPick,
}) => {
  const { token } = useContext(AppStateContext);

  const [input, setInput] = useState("");
  const [results, setResults] = useState<OrderHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every new search so a slow response for an old term cannot
  // overwrite the results for the current one.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (order) setInput(order.orderNo);
  }, [order]);

  const search = useCallback(
    (term: string) => {
      const reqId = ++requestIdRef.current;
      setLoading(true);
      apiGetOrders(token, 1, 9999, "id", "desc", undefined, term)
        .then((res) => {
          if (reqId !== requestIdRef.current || res.items.length === 0) return;
          setResults(
            res.items
              .filter((o) =>
                o.orderNo.toLowerCase().includes(term.toLowerCase())
              )
              .map((o) => ({
                id: o.id,
                orderNo: o.orderNo,
                firstName: o.firstName,
                lastName: o.lastName,
              }))
          );
          setOpen(true);
        })
        .catch(() => {
          if (reqId !== requestIdRef.current) return;
          setResults([]);
          setOpen(false);
        })
        .finally(() => {
          if (reqId === requestIdRef.current) setLoading(false);
        });
    },
    [token]
  );

  const handleType = (value: string) => {
    setInput(value);
    setError("");
    onPick(undefined);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    ++requestIdRef.current; // drop anything already in flight

    if (!value.trim()) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => search(value.trim()), 300);
  };

  const handleSelect = (orderId: number) => {
    setLoading(true);
    setOpen(false);
    apiGetOrder(token, orderId)
      .then((res) => {
        onPick(res);
        setInput(res.orderNo);
        setResults([]);
        setError("");
      })
      .catch(() => setError(`Order #${orderId} not found`))
      .finally(() => setLoading(false));
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div style={{ position: "relative" }}>
        <FormControl variant="standard" fullWidth error={!!error}>
          <InputLabel>Order # (optional)</InputLabel>
          <Input
            value={input}
            onChange={(e) => handleType(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
            endAdornment={
              loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined
            }
          />
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>

        {open && results.length > 0 && (
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            <List dense disablePadding>
              {results.map((hit) => (
                <ListItem key={hit.id} disablePadding>
                  <ListItemButton onClick={() => handleSelect(hit.id)}>
                    <ListItemText
                      primary={hit.orderNo}
                      secondary={`${hit.firstName} ${hit.lastName}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default InquiryOrderPicker;
